import React, { useMemo, type CSSProperties } from 'react';
import { LazyMotion, domAnimation, m } from 'framer-motion';
import { ChevronDown, ChevronUp } from './icons';
import { PickerBody, type PickerBodyProps } from './CollapsiblePicker.pickerBody';
import type { CollapsiblePickerTheme } from './types';

export type CSSVariableStyles = CSSProperties & Record<`--${string}`, string>;

export interface LabelProps {
    label: React.ReactNode;
    labelId: string;
    helperText?: React.ReactNode;
    helperTextId: string;
    lastValue?: number;
    currentValue?: number;
    unit?: string;
    onUseLastValue?: () => void;
}

export interface AriaProps {
    controlId: string;
    ariaDescribedBy?: string;
    ariaValueMin?: number;
    ariaValueMax?: number;
    ariaValueNow?: number;
    ariaValueText?: string;
    pickerWindowId: string;
}

export interface SurfaceRefs {
    wrapperRef: React.RefObject<HTMLDivElement | null>;
    interactiveRef: React.RefObject<HTMLDivElement | null>;
    pickerRef: React.RefObject<HTMLDivElement | null>;
    highlightRef: React.RefObject<HTMLDivElement | null>;
}

export interface LayoutProps {
    collapsedHeight: number;
    pickerWindowHeight: number;
    pickerTranslate?: number;
}

export interface PickerStateProps {
    showPicker: boolean;
    selectedIndex: number;
    totalValues: number;
}

export interface InteractionHandlers {
    onPointerDown: (event: React.PointerEvent) => void;
    onKeyDown: React.KeyboardEventHandler;
}

export interface ValueDisplayProps {
    valueNode: React.ReactNode;
    maxSampleString: string;
    unit?: string;
}

export interface QuickNumberPresenterViewModel {
    labelProps: LabelProps;
    ariaProps: AriaProps;
    refs: SurfaceRefs;
    layout: LayoutProps;
    pickerState: PickerStateProps;
    handlers: InteractionHandlers;
    highlightTapHandlers: React.HTMLAttributes<HTMLDivElement>;
    pickerBodyProps: PickerBodyProps;
    valueDisplay: ValueDisplayProps;
    cssVariables: CSSVariableStyles;
    theme: CollapsiblePickerTheme;
}

interface CollapsiblePickerPresenterProps {
    viewModel: QuickNumberPresenterViewModel;
}

/**
 * Visual shell for the quick number input, rendering the closed display,
 * picker surface, highlight chrome, and helper controls using a prebuilt view model.
 *
 * Memoized to prevent unnecessary re-renders when parent updates but viewModel is unchanged.
 *
 * @component
 * @param {CollapsiblePickerPresenterProps} props
 * @returns {React.ReactElement}
 */
export const CollapsiblePickerPresenter = React.memo(function CollapsiblePickerPresenter({ viewModel }: CollapsiblePickerPresenterProps) {
    const {
        labelProps,
        ariaProps,
        refs,
        layout,
        pickerState,
        handlers,
        highlightTapHandlers,
        pickerBodyProps,
        valueDisplay,
        cssVariables,
        theme,
    } = viewModel;
    const { label, labelId, helperText, helperTextId, lastValue, currentValue, unit, onUseLastValue } =
        labelProps;
    const {
        controlId,
        ariaDescribedBy,
        ariaValueMin,
        ariaValueMax,
        ariaValueNow,
        ariaValueText,
        pickerWindowId,
    } = ariaProps;
    const { wrapperRef, interactiveRef, pickerRef, highlightRef } = refs;
    const { collapsedHeight, pickerWindowHeight, pickerTranslate } = layout;
    const { showPicker, selectedIndex, totalValues } = pickerState;
    const { onPointerDown, onKeyDown } = handlers;
    const { valueNode, maxSampleString } = valueDisplay;
    const closedHasValue = currentValue !== undefined;

    // Memoize inline styles to prevent unnecessary re-renders
    const closedDisplayStyle = useMemo<CSSProperties>(
        () => ({
            opacity: showPicker ? 0 : 1,
            visibility: showPicker ? ('hidden' as const) : ('visible' as const),
            pointerEvents: 'none' as const,
            transition: 'opacity 0.2s',
        }),
        [showPicker]
    );

    const pickerSurfaceStyle = useMemo<CSSProperties>(
        () => ({
            pointerEvents: 'auto' as const,
            // Higher z-index when closed to receive initial click, lower when open to allow PickerColumn drag
            zIndex: showPicker ? 5 : 15,
            cursor: showPicker ? 'grab' : 'pointer',
            // Set explicit height to match visible bounds (prevents extended hitbox)
            height: showPicker ? `${pickerWindowHeight}px` : `${collapsedHeight}px`,
            transform: showPicker ? `translateY(${pickerTranslate}px)` : undefined,
            transition: 'transform 0.2s ease-out',
            userSelect: 'none' as const,
        }),
        [pickerWindowHeight, pickerTranslate, showPicker, collapsedHeight]
    );

    const motionDivStyle = useMemo<CSSProperties>(
        () => ({
            backgroundColor: theme.fadeColor,
            borderColor: theme.highlightBorderColor,
            borderWidth: 2,
            borderStyle: 'solid',
            height: `${pickerWindowHeight}px`,
            transformOrigin: 'top',
            // Lower z-index than picker-surface so picker-surface can receive initial click when closed
            zIndex: showPicker ? 10 : 0,
            // Always auto - PickerColumn will conditionally capture based on isPickerOpen
            pointerEvents: 'auto' as const,
        }),
        [pickerWindowHeight, theme.fadeColor, theme.highlightBorderColor, showPicker]
    );

    const pickerWindowStyle = useMemo<CSSProperties>(
        () => ({ height: `${pickerWindowHeight}px` }),
        [pickerWindowHeight]
    );

    return (
        <LazyMotion features={domAnimation} strict>
            <div className="quick-number-input-root space-y-2" style={cssVariables}>
            <div className="flex items-center gap-2">
                <label
                    className="font-archivo text-sm uppercase tracking-wider"
                    style={{ color: theme.labelColor }}
                    htmlFor={controlId}
                    id={labelId}
                >
                    {label}
                </label>
                {lastValue !== undefined && currentValue !== lastValue && (
                    <button
                        type="button"
                        onClick={onUseLastValue}
                        className="font-archivo text-sm transition-colors"
                        style={{ color: theme.lastValueButtonColor }}
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                    >
                        ↺ LAST: {lastValue}
                        {unit}
                    </button>
                )}
            </div>

            <div className="relative" style={{ height: `${collapsedHeight}px` }} ref={wrapperRef} data-testid="qni-wrapper">
                <div
                    className="absolute top-0 left-0 right-0"
                    data-testid="qni-closed"
                    aria-hidden={showPicker}
                    style={closedDisplayStyle}
                >
                    <div
                        className="w-full px-4 transition-all flex items-center justify-center relative"
                        style={{
                            height: `${collapsedHeight}px`,
                            borderWidth: 2,
                            borderStyle: 'solid',
                            borderColor: closedHasValue ? theme.closedBorderColor : theme.closedBorderColorEmpty,
                            backgroundColor: closedHasValue ? theme.closedBackgroundColor : theme.closedBackgroundColorEmpty,
                        }}
                    >
                        <div className="qni-closed">
                            <span className="qni-ghost" aria-hidden>
                                {maxSampleString} {unit}
                            </span>
                            <div className="qni-inner">
                                <ChevronDown className="qni-chevron" />
                                <span className="qni-value">{valueNode}</span>
                                {unit && <span className="qni-unit">{unit}</span>}
                                <ChevronUp className="qni-chevron" />
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className="absolute top-0 left-0 right-0 overflow-hidden focus:outline-hidden picker-surface"
                    style={pickerSurfaceStyle}
                    id={controlId}
                    ref={interactiveRef}
                    role="spinbutton"
                    data-testid="picker-surface"
                    data-state={showPicker ? 'open' : 'closed'}
                    tabIndex={0}
                    aria-labelledby={labelId}
                    aria-describedby={ariaDescribedBy}
                    aria-valuemin={ariaValueMin}
                    aria-valuemax={ariaValueMax}
                    aria-valuenow={ariaValueNow}
                    aria-valuetext={ariaValueText}
                    aria-controls={pickerWindowId}
                    aria-expanded={showPicker}
                    aria-activedescendant={
                        showPicker && totalValues > 0
                            ? `picker-value-option-${selectedIndex}`
                            : undefined
                    }
                    onPointerDown={onPointerDown}
                    onKeyDown={onKeyDown}
                >
                    <m.div
                        aria-hidden={!showPicker}
                        animate={{
                            opacity: showPicker ? 1 : 0,
                            scaleY: showPicker ? 1 : collapsedHeight / pickerWindowHeight,
                        }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                        style={motionDivStyle}
                    >
                        <div
                            className="w-full relative"
                            style={pickerWindowStyle}
                            ref={pickerRef}
                            data-testid="picker-window"
                            id={pickerWindowId}
                            aria-hidden={!showPicker}
                            {...highlightTapHandlers}
                        >
                            <div className="picker-fade-top" />
                            <div className="picker-highlight-fill" aria-hidden />
                            <div className="picker-highlight picker-highlight-hitbox" ref={highlightRef} aria-hidden />
                            <div className="picker-fade-bottom" />
                            <div className="picker-container">
                                <PickerBody {...pickerBodyProps} />
                            </div>
                        </div>
                    </m.div>
                </div>
            </div>

            {helperText && (
                <p className="text-xs text-white/60 font-archivo leading-relaxed" id={helperTextId}>
                    {helperText}
                </p>
            )}
            </div>
        </LazyMotion>
    );
});
